import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { isMobile } from '@/biz/tool';
import WebModal from '../Modal';
import { useWebModalStore } from '@/hooks/useWebModalStore';
import {
  CloseBgIcon,
  InstagramPlatformIcon,
  TiktokPlatformIcon
} from '@/components/common/icon';
import { options, mobileOptions } from './config';
import BottomDrawer from '../BottomDrawer';
import { Button } from 'aha-components';

interface Props {}

const ModalAuthorizationCode: React.FC<Props> = ({}) => {
  const { t } = useTranslation();
  const { showAuthorizationCode, setShowAuthorizationCode } =
    useWebModalStore();

  const [platform, setPlatform] = useState('tt');

  return (
    <>
      {isMobile() ? (
        <BottomDrawer
          isShowHeader={false}
          isShowFooter={false}
          visible={showAuthorizationCode}
        >
          <div className={`h-[calc(100vh-120px)] bg-white w-full`}>
            <div className="">
              <div className="self-stretch justify-start text-black text-2xl font-semibold font-['Geist'] leading-loose">
                {t('modal.authorization_code.title', {
                  defaultValue: 'Ad authorization code'
                })}
              </div>
              <div className="w-full mt-6 justify-start text-text-color-secondary text-sm font-semibold font-['Plus_Jakarta_Sans'] leading-relaxed">
                {t('modal.authorization_code.what_is_subtitle', {
                  defaultValue: 'What is a "ad authorization code"'
                })}
              </div>
              <div className="w-full mt-4 justify-start text-text-color-secondary text-sm font-normal font-['Inter'] leading-snug">
                {t('modal.authorization_code.description_p1', {
                  defaultValue:
                    'On platforms like TikTok and Instagram, an ad authorization code is a unique code created by a content creator to let a brand promote their original content as an ad.'
                })}
                <br />
                <br />
                {t('modal.authorization_code.description_p2', {
                  defaultValue:
                    "With this code, advertisers can boost the creator's post directly."
                })}
              </div>
            </div>
            <div className="flex justify-start items-center gap-4 mt-6">
              <div className="flex-1 h-0 outline outline-1 outline-offset-[-0.50px] outline-[#eee]" />
              <div className="inline-flex flex-col justify-start items-start gap-4">
                <div className="inline-flex justify-center items-center gap-1">
                  <div className="justify-start text-icon-color-secondary text-sm font-semibold font-['Inter'] leading-snug">
                    {t('modal.authorization_code.how_to_get_subtitle', {
                      defaultValue: 'How to get an ad authorization code'
                    })}
                  </div>
                </div>
              </div>
              <div className="flex-1 h-0 outline outline-1 outline-offset-[-0.50px] outline-[#eee]" />
            </div>
            <div className="flex items-center gap-4 mt-6">
              <div
                className={`rounded-[40px] flex justify-center items-center h-[34px] flex-1 cursor-pointer border ${platform === 'tt' ? 'bg-[#EFF1FF] border-transparent' : 'border-[#E5E5E5]'}`}
                onClick={() => setPlatform('tt')}
              >
                <TiktokPlatformIcon className="w-[18px] h-[18px]" />
              </div>
              <div
                className={`rounded-[40px] flex justify-center items-center h-[34px] flex-1 cursor-pointer border ${platform === 'ins' ? 'bg-[#EFF1FF] border-transparent' : 'border-[#E5E5E5]'}`}
                onClick={() => setPlatform('ins')}
              >
                <InstagramPlatformIcon className="w-[18px] h-[18px]" />
              </div>
            </div>
            <div className="mt-4 w-full">
              <img
                src={mobileOptions?.[platform]?.img}
                alt=""
                className="w-full"
              />
            </div>
            <div className="flex justify-start items-center gap-4 mt-6">
              <div className="flex-1 h-0 outline outline-1 outline-offset-[-0.50px] outline-zinc-100" />
              <div className="inline-flex flex-col justify-start items-start gap-4">
                <div className="inline-flex justify-center items-center gap-1">
                  <div className="justify-start text-icon-color-secondary text-sm font-medium font-['Inter'] leading-snug">
                    {t('modal.authorization_code.important_notes_subtitle', {
                      defaultValue: 'Important notes'
                    })}
                  </div>
                </div>
              </div>
              <div className="flex-1 h-0 outline outline-1 outline-offset-[-0.50px] outline-zinc-100" />
            </div>
            <div className="flex flex-col gap-4 mt-6">
              {mobileOptions?.[platform]?.notes?.map((item: string) => (
                <li key={item}>
                  <span
                    dangerouslySetInnerHTML={{ __html: item }}
                    className="text-[#1c2024] text-base font-normal font-['Inter'] leading-7"
                  ></span>
                </li>
              ))}
            </div>
            <div className="w-full mt-4 p-4 bg-white border-t border-component-divider inline-flex justify-center items-center gap-3">
              <Button
                type="primary"
                className="w-full"
                onClick={() => setShowAuthorizationCode(false)}
              >
                {t('modal.authorization_code.close_btn', {
                  defaultValue: 'Close'
                })}
              </Button>
            </div>
          </div>
        </BottomDrawer>
      ) : (
        <WebModal
          visible={showAuthorizationCode}
          isShowFooter={false}
          isShowHeader={false}
          width={1200}
          className=" bg-[#F6F6F4] p-0 rounded-[24px] overflow-hidden"
        >
          <div className="flex items-stretch justify-between w-full">
            <div className="w-[380px] p-8 bg-[#F3F4FB]">
              <div className="w-80 justify-start text-text-color-secondary text-xl font-semibold font-['Plus_Jakarta_Sans'] leading-relaxed">
                {t('modal.authorization_code.head_tutorial_label', {
                  defaultValue: 'AhaCreator tutorial'
                })}
              </div>
              <div className="w-80 mt-4 justify-start text-black text-3xl font-semibold font-['Plus_Jakarta_Sans'] leading-9">
                {t('modal.authorization_code.title', {
                  defaultValue: 'Ad authorization code'
                })}
              </div>
              <div className="self-stretch mt-[48px] inline-flex flex-col justify-center items-center gap-2.5">
                <div className="w-80 justify-start text-text-color-secondary text-xl font-semibold font-['Plus_Jakarta_Sans'] leading-relaxed">
                  {t('modal.authorization_code.what_is_subtitle', {
                    defaultValue: 'What is a "ad authorization code"'
                  })}
                </div>
                <div className="w-80 justify-start text-text-color-secondary text-sm font-normal font-['Inter'] leading-snug">
                  {t('modal.authorization_code.description_p1', {
                    defaultValue:
                      'On platforms like TikTok and Instagram, an ad authorization code is a unique code created by a content creator to let a brand promote their original content as an ad.'
                  })}
                  <br />
                  <br />
                  {t('modal.authorization_code.description_p2', {
                    defaultValue:
                      "With this code, advertisers can boost the creator's post directly."
                  })}
                </div>
                <div className="self-stretch mt-[48px] inline-flex flex-col justify-center items-center gap-2.5">
                  <div className="w-80 justify-start text-text-color-secondary text-xl font-semibold font-['Plus_Jakarta_Sans'] leading-relaxed">
                    {t('modal.authorization_code.how_it_works_subtitle', {
                      defaultValue: 'How it works'
                    })}
                  </div>
                  <div className="w-80 justify-start text-text-color-secondary text-sm font-normal font-['Inter'] leading-snug">
                    {t('modal.authorization_code.how_it_works_step1', {
                      defaultValue:
                        '1. The influencer enables ad authorization on their post.'
                    })}
                    <br />
                    {t('modal.authorization_code.how_it_works_step2', {
                      defaultValue: '2. They generate a code within the app.'
                    })}
                    <br />
                    {t('modal.authorization_code.how_it_works_step3', {
                      defaultValue:
                        '3. The advertiser enters this code in the ad platform to access and promote the post.'
                    })}
                  </div>
                </div>
              </div>
            </div>
            <div className="flex-1">
              <div className="w-full px-8 py-6 border-b border-component-divider flex justify-between items-center">
                <div className="justify-start text-text-color-primary text-xl font-semibold font-['Plus_Jakarta_Sans'] leading-normal">
                  {t('modal.authorization_code.how_to_get_subtitle', {
                    defaultValue: 'How to get an ad authorization code'
                  })}
                </div>
                <div
                  className="cursor-pointer"
                  onClick={() => setShowAuthorizationCode(false)}
                >
                  <CloseBgIcon />
                </div>
              </div>
              <div className="p-8">
                <div className="flex items-center gap-4">
                  <div
                    className={`rounded-[40px] flex justify-center items-center gap-2 h-[40px] flex-1 cursor-pointer border ${platform === 'tt' ? 'bg-[#EFF1FF] border-transparent' : 'border-[#E5E5E5]'}`}
                    onClick={() => setPlatform('tt')}
                  >
                    <TiktokPlatformIcon className="w-[18px] h-[18px]" />
                    <div className="justify-start text-text-color-primary text-base font-semibold font-['Plus_Jakarta_Sans'] leading-loose">
                      TikTok
                    </div>
                  </div>
                  <div
                    className={`rounded-[40px] flex justify-center items-center gap-2 h-[40px] flex-1 cursor-pointer border ${platform === 'ins' ? 'bg-[#EFF1FF] border-transparent' : 'border-[#E5E5E5]'}`}
                    onClick={() => setPlatform('ins')}
                  >
                    <InstagramPlatformIcon className="w-[18px] h-[18px]" />
                    <div className="justify-start text-text-color-primary text-base font-semibold font-['Plus_Jakarta_Sans'] leading-loose">
                      Instagram
                    </div>
                  </div>
                </div>
                <div className="mt-4 w-full">
                  <img
                    src={options?.[platform]?.img}
                    alt=""
                    className="w-full"
                  />
                </div>
                <div className="flex flex-col gap-4 mt-6">
                  {options?.[platform]?.notes?.map((item: string) => (
                    <li key={item}>
                      <span
                        dangerouslySetInnerHTML={{ __html: item }}
                        className="text-[#1c2024] text-base font-normal font-['Inter'] leading-7"
                      ></span>
                    </li>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </WebModal>
      )}
    </>
  );
};

export default ModalAuthorizationCode;
